/**
 * Blog manifest — single source of truth for all published posts.
 * Front-matter metadata (slug, lang, title, description, keywords, date) is
 * inlined here so the Blog index page can render instantly without N round-
 * trips. The Markdown body is fetched on-demand from /content/blog/{slug}.md
 * by the post page.
 *
 * Adding a new post:
 *   1. Drop the .md file into /app/frontend/public/content/blog/
 *   2. Append a single object to POSTS below.
 *
 * Bonus: the Manual page reads this list to surface "New on the blog".
 */
export const POSTS = [
  // ===== ENGLISH (primary) =====
  {
    slug: 'best-basketball-playbook-for-coaches',
    lang: 'en',
    title: 'Best Basketball Playbook for Coaches in 2026',
    description:
      "Discover the best basketball playbook for coaches: drag-and-drop play creation, motion offense, Press Break, Horns and PnR systems — all in one app.",
    keywords:
      'basketball playbook, best basketball playbook, basketball coaching, basketball strategy, basketball offense plays, basketball defense plays',
    date: '2026-02-20',
  },
  {
    slug: 'how-to-create-a-modern-basketball-playbook',
    lang: 'en',
    title: 'How to Create a Modern Basketball Playbook (Step-by-Step)',
    description:
      'A practical step-by-step on how to create a basketball playbook in 2026: structure, key sets, drills, and the digital tools coaches actually use.',
    keywords:
      'how to create a basketball playbook, basketball coaching playbook, digital basketball playbook, basketball play creator, basketball set plays',
    date: '2026-02-20',
  },
  {
    slug: 'simple-basketball-plays-for-youth-teams',
    lang: 'en',
    title: 'Simple Basketball Plays for Youth Teams (U10-U16)',
    description:
      'Simple, age-appropriate basketball plays for youth teams (U10-U16): spacing, motion fundamentals, BLOB/SLOB, and quick hitters that actually work.',
    keywords:
      'simple basketball plays for youth teams, youth basketball plays, basketball plays for beginners, basketball drills, basketball fundamentals',
    date: '2026-02-20',
  },
  {
    slug: 'press-break-explained',
    lang: 'en',
    title: 'Press Break Explained: How to Beat the Full-Court Press',
    description:
      "Press break explained step-by-step: the spacing, reads and roles that beat 1-2-1-1, 2-2-1 and trap presses at every level.",
    keywords:
      'press break, basketball press break, beat full court press, basketball inbound plays, basketball coaching app',
    date: '2026-02-20',
  },
  {
    slug: 'best-basketball-coaching-app',
    lang: 'en',
    title: 'Best Basketball Coaching App: PlayMaster IA Review 2026',
    description:
      'A 2026 hands-on review of the best basketball coaching app: play creator, AI assistant, roster, marketplace, live game mode and direct install PWA.',
    keywords:
      'best basketball coaching app, basketball coaching app, basketball play creator, basketball play designer, interactive basketball playbook',
    date: '2026-02-20',
  },

  // ===== ESPAÑOL (secondary) =====
  {
    slug: 'el-mejor-playbook-de-basketball-para-entrenadores',
    lang: 'es',
    title: 'El Mejor Playbook de Basketball para Entrenadores en 2026',
    description:
      'El mejor playbook de basketball para entrenadores: creación drag-and-drop, jugadas ofensivas y defensivas, Press Break, Horns, motion y mucho más.',
    keywords:
      'el mejor playbook de basketball, playbook para entrenadores, playbook táctico de basketball, jugadas de basketball, app para entrenadores',
    date: '2026-02-20',
  },
  {
    slug: 'como-crear-un-playbook-moderno',
    lang: 'es',
    title: 'Cómo Crear un Playbook Moderno de Basketball (Paso a Paso)',
    description:
      'Guía paso a paso para crear un playbook moderno de basketball en 2026: estructura, sets clave, drills y la herramienta digital que usan los coaches top.',
    keywords:
      'cómo crear un playbook, playbook moderno, playbook digital de basketball, crear jugadas de basketball, app para crear jugadas',
    date: '2026-02-20',
  },
  {
    slug: 'jugadas-faciles-para-equipos-juveniles',
    lang: 'es',
    title: 'Jugadas Fáciles para Equipos Juveniles de Basketball (Sub-10 a Sub-16)',
    description:
      'Jugadas fáciles para equipos juveniles (Sub-10 a Sub-16): spacing, motion básico, BLOB/SLOB y quick hitters apropiados para cada edad.',
    keywords:
      'jugadas fáciles de basketball, jugadas para equipos juveniles, jugadas para principiantes, playbook juvenil de basketball, jugadas para niños',
    date: '2026-02-20',
  },
  {
    slug: 'press-break-explicado-paso-a-paso',
    lang: 'es',
    title: 'Press Break Explicado Paso a Paso: Cómo Romper la Presión a Toda Cancha',
    description:
      'Press break explicado paso a paso: el spacing, las lecturas y los roles que rompen la presión 1-2-1-1, 2-2-1 y trap en cualquier nivel.',
    keywords:
      'press break, romper presión, romper presión a toda cancha, jugadas defensivas, jugadas de basketball, app para entrenadores',
    date: '2026-02-20',
  },
  {
    slug: 'app-para-entrenadores-de-basketball',
    lang: 'es',
    title: 'App para Entrenadores de Basketball: Reseña de PlayMaster IA 2026',
    description:
      'Reseña 2026 de la mejor app para entrenadores de basketball: creador de jugadas, IA, roster, marketplace, modo en vivo e instalación directa.',
    keywords:
      'app para entrenadores de basketball, app de jugadas de basketball, app para crear jugadas, mejor app de basketball, playbook interactivo',
    date: '2026-02-20',
  },

  // ===== BATCH 2 — Tactical deep dives (Feb 2026) =====
  {
    slug: 'ato-plays-after-timeout-design',
    lang: 'en',
    title: 'Designing Killer ATO Plays: Frameworks That Free Shooters',
    description:
      'The Two-Second Window That Wins…',
    keywords:
      'pick and roll defense, horns plays, motion offense, flex offense, Princeton offense, transition offense, BLOB plays, SLOB plays',
    date: '2026-02-24',
  },
  {
    slug: 'basketball-film-study-workflow',
    lang: 'en',
    title: 'Basketball Film Study Workflow: From Raw Game to Actionable Clips',
    description:
      'Why Most Coaches Waste Hours Rewatching Full…',
    keywords:
      'pick and roll defense, horns plays, motion offense, flex offense, Princeton offense, transition offense, BLOB plays, SLOB plays',
    date: '2026-02-24',
  },
  {
    slug: 'basketball-game-planning-step-by-step',
    lang: 'en',
    title: 'Basketball Game Planning Step-by-Step: Scouting to Whiteboard',
    description:
      'Every second counts between the final buzzer of your last game and tip-off of the next—and how you spend that time determines whether your team walks in…',
    keywords:
      'pick and roll defense, horns plays, motion offense, flex offense, Princeton offense, transition offense, BLOB plays, SLOB plays',
    date: '2026-02-24',
  },
  {
    slug: 'best-blob-plays-end-of-game',
    lang: 'en',
    title: '10 Best BLOB Plays for End-of-Game Situations',
    description:
      'Why Late-Game BLOB Sets Win…',
    keywords:
      'pick and roll defense, horns plays, motion offense, flex offense, Princeton offense, transition offense, BLOB plays, SLOB plays',
    date: '2026-02-24',
  },
  {
    slug: 'best-cutting-actions-half-court',
    lang: 'en',
    title: 'Best Cutting Actions in the Half Court: Backdoors, UCLA, Iverson',
    description:
      'Why Great Teams Score Without the…',
    keywords:
      'pick and roll defense, horns plays, motion offense, flex offense, Princeton offense, transition offense, BLOB plays, SLOB plays',
    date: '2026-02-24',
  },
  {
    slug: 'best-slob-plays-coaches-rely-on',
    lang: 'en',
    title: 'Best SLOB Plays Top Coaches Rely On in Tight Games',
    description:
      'Why SLOB Plays Win Close…',
    keywords:
      'pick and roll defense, horns plays, motion offense, flex offense, Princeton offense, transition offense, BLOB plays, SLOB plays',
    date: '2026-02-24',
  },
  {
    slug: 'como-atacar-la-zona-2-3',
    lang: 'es',
    title: 'Cómo Atacar la Zona 2-3: 5 Frameworks Ofensivos Probados',
    description:
      'Por Qué la Zona 2-3 Sigue Siendo un Desafío…',
    keywords:
      'defensa del pick and roll, jugadas horns, ofensiva motion, ofensiva flex, ofensiva Princeton, ofensiva en transición, jugadas BLOB, jugadas SLOB',
    date: '2026-02-24',
  },
  {
    slug: 'defensa-del-pick-and-roll-coberturas',
    lang: 'es',
    title: 'Defensa del Pick and Roll: Drop, Hedge, Switch e ICE Explicados',
    description:
      'Por qué la cobertura del PnR define partidos…',
    keywords:
      'defensa del pick and roll, jugadas horns, ofensiva motion, ofensiva flex, ofensiva Princeton, ofensiva en transición, jugadas BLOB, jugadas SLOB',
    date: '2026-02-24',
  },
  {
    slug: 'defensive-drills-for-youth-coaches',
    lang: 'en',
    title: 'Essential Defensive Drills Every Youth Coach Should Run',
    description:
      'Why Defense Wins Championships—Especially at the Youth…',
    keywords:
      'pick and roll defense, horns plays, motion offense, flex offense, Princeton offense, transition offense, BLOB plays, SLOB plays',
    date: '2026-02-24',
  },
  {
    slug: 'desarrollar-iq-baloncesto-en-jugadores',
    lang: 'es',
    title: 'Desarrollar IQ de Baloncesto: Drills, Decisiones y Frameworks',
    description:
      'El corazón del baloncesto moderno no late en el gimnasio de pesas ni en la pista de velocidad, sino en el espacio milimétrico entre las orejas de cada…',
    keywords:
      'defensa del pick and roll, jugadas horns, ofensiva motion, ofensiva flex, ofensiva Princeton, ofensiva en transición, jugadas BLOB, jugadas SLOB',
    date: '2026-02-24',
  },
  {
    slug: 'developing-basketball-iq-in-players',
    lang: 'en',
    title: 'Developing Basketball IQ: Drills, Decisions, and Frameworks',
    description:
      'Why Basketball IQ Separates Good Players from Great…',
    keywords:
      'pick and roll defense, horns plays, motion offense, flex offense, Princeton offense, transition offense, BLOB plays, SLOB plays',
    date: '2026-02-24',
  },
  {
    slug: 'disenar-jugadas-ato-letales',
    lang: 'es',
    title: 'Diseñar Jugadas ATO Letales: Frameworks para Liberar Tiradores',
    description:
      'El Arte de Robar Posesiones…',
    keywords:
      'defensa del pick and roll, jugadas horns, ofensiva motion, ofensiva flex, ofensiva Princeton, ofensiva en transición, jugadas BLOB, jugadas SLOB',
    date: '2026-02-24',
  },
  {
    slug: 'drills-defensivos-para-coaches-juveniles',
    lang: 'es',
    title: 'Drills Defensivos Esenciales para Coaches Juveniles',
    description:
      'La defensa es el fundamento que separa a los equipos juveniles disciplinados de aquellos que dependen únicamente del talento individual. Un coach que…',
    keywords:
      'defensa del pick and roll, jugadas horns, ofensiva motion, ofensiva flex, ofensiva Princeton, ofensiva en transición, jugadas BLOB, jugadas SLOB',
    date: '2026-02-24',
  },
  {
    slug: 'film-study-de-baloncesto-flujo-de-trabajo',
    lang: 'es',
    title: 'Film Study de Baloncesto: Del Partido Crudo a Clips Accionables',
    description:
      'Cada posesión que grabas contiene decenas de decisiones tácticas, pero sin un sistema claro para extraer y clasificar esas pepitas de oro, tu film study…',
    keywords:
      'defensa del pick and roll, jugadas horns, ofensiva motion, ofensiva flex, ofensiva Princeton, ofensiva en transición, jugadas BLOB, jugadas SLOB',
    date: '2026-02-24',
  },
  {
    slug: 'ghost-screens-en-el-baloncesto-moderno',
    lang: 'es',
    title: 'Ghost Screens: La Acción Moderna que Rompe los Switches',
    description:
      'El switch defensivo se ha convertido en el arma predilecta de la mayoría de equipos modernos ante el pick and roll, pero existe una acción que está…',
    keywords:
      'defensa del pick and roll, jugadas horns, ofensiva motion, ofensiva flex, ofensiva Princeton, ofensiva en transición, jugadas BLOB, jugadas SLOB',
    date: '2026-02-24',
  },
  {
    slug: 'ghost-screens-in-modern-basketball',
    lang: 'en',
    title: 'Ghost Screens: The Modern Action That Breaks Switches',
    description:
      'What Makes Ghost Screens Lethal Against Switching…',
    keywords:
      'pick and roll defense, horns plays, motion offense, flex offense, Princeton offense, transition offense, BLOB plays, SLOB plays',
    date: '2026-02-24',
  },
  {
    slug: 'horns-offense-complete-guide',
    lang: 'en',
    title: 'Horns Offense Complete Guide: Sets, Reads & Variations',
    description:
      'What Is the Horns Offense and Why Top Programs Run…',
    keywords:
      'pick and roll defense, horns plays, motion offense, flex offense, Princeton offense, transition offense, BLOB plays, SLOB plays',
    date: '2026-02-24',
  },
  {
    slug: 'how-to-attack-a-2-3-zone',
    lang: 'en',
    title: 'How to Attack a 2-3 Zone: 5 Proven Offensive Frameworks',
    description:
      'Why Traditional Set Plays Often Fail Against the 2-3…',
    keywords:
      'pick and roll defense, horns plays, motion offense, flex offense, Princeton offense, transition offense, BLOB plays, SLOB plays',
    date: '2026-02-24',
  },
  {
    slug: 'jugadas-finales-gestion-del-reloj',
    lang: 'es',
    title: 'Jugadas Finales y Gestión del Reloj para Coaches',
    description:
      'Los últimos segundos de un partido separán a los equipos buenos de los grandes, y tu capacidad como entrenador para ejecutar y gestionar estos momentos…',
    keywords:
      'defensa del pick and roll, jugadas horns, ofensiva motion, ofensiva flex, ofensiva Princeton, ofensiva en transición, jugadas BLOB, jugadas SLOB',
    date: '2026-02-24',
  },
  {
    slug: 'late-game-plays-clock-management',
    lang: 'en',
    title: 'Late-Game Plays and Clock Management for Coaches',
    description:
      'Understanding the Critical Final…',
    keywords:
      'pick and roll defense, horns plays, motion offense, flex offense, Princeton offense, transition offense, BLOB plays, SLOB plays',
    date: '2026-02-24',
  },
  {
    slug: 'mejores-acciones-de-corte-media-cancha',
    lang: 'es',
    title: 'Mejores Acciones de Corte en Media Cancha: Backdoor, UCLA, Iverson',
    description:
      'Por Qué los Cortes en Media Cancha Ganan…',
    keywords:
      'defensa del pick and roll, jugadas horns, ofensiva motion, ofensiva flex, ofensiva Princeton, ofensiva en transición, jugadas BLOB, jugadas SLOB',
    date: '2026-02-24',
  },
  {
    slug: 'mejores-jugadas-blob-final-de-partido',
    lang: 'es',
    title: '10 Mejores Jugadas BLOB para Situaciones de Final de Partido',
    description:
      'En los momentos cruciales de un partido, cuando cada posesión puede decidir el resultado, las jugadas BLOB bien ejecutadas se convierten en el arma…',
    keywords:
      'defensa del pick and roll, jugadas horns, ofensiva motion, ofensiva flex, ofensiva Princeton, ofensiva en transición, jugadas BLOB, jugadas SLOB',
    date: '2026-02-24',
  },
  {
    slug: 'mejores-jugadas-slob-para-coaches',
    lang: 'es',
    title: 'Las Mejores Jugadas SLOB en las que Confían los Coaches',
    description:
      'Por Qué las Jugadas SLOB Marcan la Diferencia en Partidos…',
    keywords:
      'defensa del pick and roll, jugadas horns, ofensiva motion, ofensiva flex, ofensiva Princeton, ofensiva en transición, jugadas BLOB, jugadas SLOB',
    date: '2026-02-24',
  },
  {
    slug: 'motion-offense-principles',
    lang: 'en',
    title: 'Motion Offense Principles: Spacing, Cutting, Reading Defenders',
    description:
      'Why Motion Offense Wins Games at Every…',
    keywords:
      'pick and roll defense, horns plays, motion offense, flex offense, Princeton offense, transition offense, BLOB plays, SLOB plays',
    date: '2026-02-24',
  },
  {
    slug: 'ofensiva-en-transicion-ritmo-y-espaciado',
    lang: 'es',
    title: 'Ofensiva en Transición: Ritmo, Espaciado y Triggers de Decisión',
    description:
      'Por Qué la Mayoría de Equipos Pierden Ventaja en…',
    keywords:
      'defensa del pick and roll, jugadas horns, ofensiva motion, ofensiva flex, ofensiva Princeton, ofensiva en transición, jugadas BLOB, jugadas SLOB',
    date: '2026-02-24',
  },
  {
    slug: 'ofensiva-horns-guia-completa',
    lang: 'es',
    title: 'Ofensiva Horns: Guía Completa de Sets, Reads y Variantes',
    description:
      'Por qué Horns domina el baloncesto…',
    keywords:
      'defensa del pick and roll, jugadas horns, ofensiva motion, ofensiva flex, ofensiva Princeton, ofensiva en transición, jugadas BLOB, jugadas SLOB',
    date: '2026-02-24',
  },
  {
    slug: 'pick-and-roll-defense-coverages',
    lang: 'en',
    title: 'Pick and Roll Defense: Drop, Hedge, Switch, ICE Coverages Explained',
    description:
      'Why Your Defense Will Never Win Without a PnR…',
    keywords:
      'pick and roll defense, horns plays, motion offense, flex offense, Princeton offense, transition offense, BLOB plays, SLOB plays',
    date: '2026-02-24',
  },
  {
    slug: 'plan-de-partido-baloncesto-paso-a-paso',
    lang: 'es',
    title: 'Plan de Partido de Baloncesto Paso a Paso: Del Scouting a la Pizarra',
    description:
      'Cada victoria empieza mucho antes del salto inicial: en la sala de vídeo, en la pizarra y en la atención al detalle. Un plan de partido bien estructurado…',
    keywords:
      'defensa del pick and roll, jugadas horns, ofensiva motion, ofensiva flex, ofensiva Princeton, ofensiva en transición, jugadas BLOB, jugadas SLOB',
    date: '2026-02-24',
  },
  {
    slug: 'principios-de-la-ofensiva-motion',
    lang: 'es',
    title: 'Principios de la Ofensiva Motion: Espaciado, Cortes y Lecturas',
    description:
      'Un sistema ofensivo sólido no se construye con jugadas estáticas, sino con principios que permiten a los jugadores leer la defensa y reaccionar en tiempo…',
    keywords:
      'defensa del pick and roll, jugadas horns, ofensiva motion, ofensiva flex, ofensiva Princeton, ofensiva en transición, jugadas BLOB, jugadas SLOB',
    date: '2026-02-24',
  },
  {
    slug: 'transition-offense-pace-and-spacing',
    lang: 'en',
    title: 'Transition Offense: Pace, Spacing, and Decision Triggers',
    description:
      'Why Most Teams Lose Games in the First Seven…',
    keywords:
      'pick and roll defense, horns plays, motion offense, flex offense, Princeton offense, transition offense, BLOB plays, SLOB plays',
    date: '2026-02-24',
  },
];

export function getPostBySlug(slug) {
  return POSTS.find((p) => p.slug === slug) || null;
}

export function postsByLang(lang) {
  return POSTS.filter((p) => p.lang === lang);
}
